import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api, PIPELINE_STAGES, stageColor, stageLabel } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Upload, Check, Plus, Trash2, FileText } from "lucide-react";

const ProjectDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [tab, setTab] = useState("overview");
  const [newShot, setNewShot] = useState("");
  const [polling, setPolling] = useState(false);
  const fileRef = useRef(null);

  const isAdmin = user?.role === "admin";
  const backLink = isAdmin ? "/admin/pipeline" : "/portal";

  const load = async () => {
    const { data } = await api.get(`/projects/${id}`);
    setProject(data);
    const f = await api.get(`/files/project/${id}`);
    setFiles(f.data);
  };
  useEffect(() => { load(); }, [id]);

  // Poll Stripe status if returning from checkout
  useEffect(() => {
    const sid = searchParams.get("session_id");
    if (!sid || polling) return;
    setPolling(true);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Payment successful");
          load();
          return;
        }
        if (data.status === "expired" || attempts >= 10) return;
        setTimeout(poll, 2000);
      } catch {}
    };
    poll();
  }, [searchParams]);

  const updateStatus = async (status) => {
    await api.patch(`/projects/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };

  const addShot = async () => {
    if (!newShot.trim()) return;
    const shot_list = [...(project.shot_list || []), { text: newShot, done: false, id: Date.now() }];
    await api.patch(`/projects/${id}`, { shot_list });
    setNewShot("");
    load();
  };
  const toggleShot = async (idx) => {
    const shot_list = project.shot_list.map((s, i) => i === idx ? { ...s, done: !s.done } : s);
    await api.patch(`/projects/${id}`, { shot_list });
    load();
  };
  const removeShot = async (idx) => {
    const shot_list = project.shot_list.filter((_, i) => i !== idx);
    await api.patch(`/projects/${id}`, { shot_list });
    load();
  };

  const saveNotes = async (notes) => {
    await api.patch(`/projects/${id}`, { notes });
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("project_id", id);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("File uploaded");
      load();
    } catch { toast.error("Upload failed"); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const pay = async (type) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        project_id: id, payment_type: type, origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) { toast.error(err.response?.data?.detail || "Checkout failed"); }
  };

  const deleteProject = async () => {
    if (!window.confirm("Delete this project permanently?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    nav(backLink);
  };

  if (!project) return <div className="p-12 text-white/50">Loading…</div>;

  return (
    <div className="min-h-screen">
      <div className="border-b border-white/5 p-8">
        <div className="max-w-6xl mx-auto">
          <Link to={backLink} className="label-caps hover:text-white transition inline-flex items-center gap-2 mb-6" data-testid="back-link">
            <ArrowLeft size={12} /> Back
          </Link>
          <div className="flex items-start justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="status-dot" style={{ background: stageColor(project.status) }} />
                <span className="label-caps">{stageLabel(project.status)}</span>
              </div>
              <h1 className="font-editorial text-5xl md:text-6xl tracking-tighter leading-none mb-3">{project.project_title}</h1>
              <div className="text-white/60">{project.client_name} · {project.client_email}</div>
            </div>
            {isAdmin && (
              <div className="flex flex-col gap-2">
                <select value={project.status} onChange={(e) => updateStatus(e.target.value)} className="input-editorial !py-2" data-testid="status-select">
                  {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button onClick={deleteProject} className="label-caps text-white/40 hover:text-red-400 transition inline-flex items-center gap-1" data-testid="delete-project">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-8">
        <div className="flex gap-8 border-b border-white/5 mb-8">
          {[["overview","Overview"],["preproduction","Pre-Production"],["files","Files"],["payments","Payments"]].map(([k,l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`label-caps py-4 border-b-2 transition ${tab===k ? "border-white text-white" : "border-transparent text-white/40 hover:text-white"}`}>
              {l}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="space-y-6">
                <Field label="Project Type" value={project.project_type} />
                <Field label="Shoot Date" value={project.shoot_date} mono />
                <Field label="Location" value={project.location} />
                <Field label="Budget" value={project.budget} />
              </div>
            </div>
            <div className="space-y-8">
              <div>
                <div className="label-caps mb-3">Vision</div>
                <p className="font-editorial text-2xl leading-relaxed text-white/80">{project.vision || "—"}</p>
              </div>
              <div>
                <div className="label-caps mb-3">Expectations</div>
                <p className="text-white/70 leading-relaxed">{project.expectations || "—"}</p>
              </div>
            </div>
          </div>
        )}

        {tab === "preproduction" && (
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <div className="label-caps mb-4">Shot List</div>
              {isAdmin && (
                <div className="flex gap-2 mb-4">
                  <input className="input-editorial" placeholder="Add a shot..." value={newShot} onChange={(e) => setNewShot(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addShot())} data-testid="shot-input"/>
                  <button onClick={addShot} className="btn-primary !px-4" data-testid="add-shot"><Plus size={14}/></button>
                </div>
              )}
              <div className="space-y-2">
                {(project.shot_list || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-white/5 group" data-testid={`shot-${i}`}>
                    {isAdmin ? (
                      <button onClick={() => toggleShot(i)} className={`w-5 h-5 border flex items-center justify-center ${s.done ? "bg-white border-white" : "border-white/30"}`}>
                        {s.done && <Check size={12} className="text-black"/>}
                      </button>
                    ) : (
                      <div className={`w-5 h-5 border flex items-center justify-center ${s.done ? "bg-white border-white" : "border-white/30"}`}>
                        {s.done && <Check size={12} className="text-black"/>}
                      </div>
                    )}
                    <span className={`flex-1 ${s.done ? "line-through text-white/30" : ""}`}>{s.text}</span>
                    {isAdmin && <button onClick={() => removeShot(i)} className="text-white/20 hover:text-red-400 opacity-0 group-hover:opacity-100"><X size={14}/></button>}
                  </div>
                ))}
                {(project.shot_list || []).length === 0 && <div className="text-white/30 text-sm">No shots yet</div>}
              </div>
            </div>
            <div>
              <div className="label-caps mb-4">Notes</div>
              <textarea
                rows={12}
                defaultValue={project.notes || ""}
                disabled={!isAdmin}
                onBlur={(e) => isAdmin && saveNotes(e.target.value)}
                className="input-editorial font-editorial text-xl"
                placeholder="Production notes..."
                data-testid="notes-area"
              />
            </div>
          </div>
        )}

        {tab === "files" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div className="label-caps">Assets ({files.length})</div>
              <label className="btn-primary inline-flex items-center gap-2 cursor-pointer" data-testid="upload-btn">
                <Upload size={14} /> Upload File
                <input ref={fileRef} type="file" className="hidden" onChange={onUpload} data-testid="file-input"/>
              </label>
            </div>
            <div className="space-y-px bg-white/5">
              {files.map((f) => (
                <div key={f.id} className="bg-[#0A0A0A] p-4 flex items-center gap-4" data-testid={`file-${f.id}`}>
                  <FileText size={16} className="text-white/40" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{f.filename}</div>
                    <div className="font-mono-alt text-xs text-white/40">
                      {(f.size / 1024).toFixed(1)} KB · {f.uploader_name}
                    </div>
                  </div>
                </div>
              ))}
              {files.length === 0 && <div className="bg-[#0A0A0A] p-12 text-center text-white/30 text-sm">No files uploaded yet</div>}
            </div>
          </div>
        )}

        {tab === "payments" && (
          <div className="grid md:grid-cols-2 gap-6">
            <PaymentCard
              label="Deposit"
              amount={project.deposit_amount}
              paid={project.deposit_paid}
              onPay={() => pay("deposit")}
              canPay={!isAdmin && project.deposit_amount > 0 && !project.deposit_paid}
              testid="pay-deposit"
            />
            <PaymentCard
              label="Final Payment"
              amount={project.final_amount}
              paid={project.final_paid}
              onPay={() => pay("final")}
              canPay={!isAdmin && project.final_amount > 0 && !project.final_paid}
              testid="pay-final"
            />
            {isAdmin && (
              <div className="md:col-span-2 p-6 border border-white/10 bg-[#0A0A0A]">
                <div className="label-caps mb-2">Admin view</div>
                <p className="text-white/50 text-sm">Clients can pay via their portal. Amounts set in project settings.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }) => (
  <div>
    <div className="label-caps mb-1">{label}</div>
    <div className={`${mono ? "font-mono-alt" : "font-editorial text-xl"} text-white/80`}>{value || "—"}</div>
  </div>
);

const PaymentCard = ({ label, amount, paid, onPay, canPay, testid }) => (
  <div className="border border-white/10 bg-[#0A0A0A] p-8" data-testid={`payment-card-${label.toLowerCase().replace(" ", "-")}`}>
    <div className="label-caps mb-4">{label}</div>
    <div className="font-editorial text-5xl tracking-tighter mb-2">${amount?.toLocaleString() || 0}</div>
    <div className="mb-6">
      {paid ? (
        <span className="label-caps text-emerald-400">✓ Paid</span>
      ) : amount > 0 ? (
        <span className="label-caps text-white/40">Pending</span>
      ) : (
        <span className="label-caps text-white/30">Not set</span>
      )}
    </div>
    {canPay && (
      <button onClick={onPay} className="btn-primary w-full" data-testid={testid}>Pay Now</button>
    )}
  </div>
);

export default ProjectDetail;
